/**
 * Guides Reducer
 *
 * Manages the global state for the guide system.
 * This allows guides to be triggered, rendered, and reacted upon from anywhere in the app.
 *
 * Flow:
 * 1. Any package can dispatch `open_manager` (optionally with a guideId) to open the guide dialog.
 * 2. When a user triggers a guide, `start_guide` is dispatched, setting `runningGuideId`.
 * 3. The Guides host component (in umbrella) renders the appropriate guide based on `runningGuideId`.
 * 4. When the guide finishes, `complete_guide` is dispatched, marking it completed and clearing the running state.
 * 5. On page reload, `getInitialGuidesState` (see ./guidesState.ts) rehydrates the persisted data
 *    (completion, discovery, selectedManagerGuideId, stepIndex) and resets the ephemeral fields
 *    (isManagerOpen, runningGuideId) so no dialog or tour is ever restored mid-session.
 */

import { GuideId } from './guideId';

/**
 * Builds a map holding the same value for every guide.
 *
 * @param value - The value to assign to each guide
 * @returns A map covering every GuideId member
 */
const buildGuideBooleanMap = (value: boolean): Record<GuideId, boolean> =>
    Object.fromEntries(
        Object.values(GuideId).map((guideId) => [guideId, value])
    ) as Record<GuideId, boolean>;

/**
 * Tracks which guides the user has completed.
 * Persisted across sessions so users see their progress retained.
 */
type GuideCompletionState = Record<GuideId, boolean>;

/**
 * Tracks which guides the user has already seen in the guide manager list.
 * Drives the "new guide" beacons, which hide themselves once discovered.
 */
type GuideDiscoveryState = Record<GuideId, boolean>;

/**
 * Tracks the current step index for each guide.
 * Reserved for future use: allows resuming a guide from where the user left off.
 */
type GuideStepIndexState = Record<GuideId, number>;

/**
 * The full state shape for the guides slice.
 * Combines ephemeral UI state (manager open, running guide) with persistent progress data.
 */
interface GuidesReducerState {
    /** Whether the guide manager dialog is currently open */
    isManagerOpen: boolean;
    /** The currently selected/highlighted guide in the manager dialog */
    selectedManagerGuideId: GuideId;
    /** The guide that is currently being played (null if no guide is active) */
    runningGuideId: GuideId | null;
    /** Per-guide completion tracking (true = user has finished this guide) */
    completionByGuide: GuideCompletionState;
    /** Per-guide discovery tracking (true = user has seen this guide, so its beacon hides) */
    discoveryByGuide: GuideDiscoveryState;
    /** Per-guide step index tracking (for future step resume support) */
    stepIndexByGuide: GuideStepIndexState;
    /** Whether the user has already seen the "all guides completed" celebration */
    isCelebrationOver: boolean;
}

/**
 * All possible actions that can be dispatched to the guides reducer.
 * Uses a discriminated union on `type` for exhaustive switch handling.
 */
type GuidesReducerAction =
    | {
          /** Changes the selected/highlighted guide in the manager (without opening/closing) */
          type: 'select_guide';
          payload: { guideId: GuideId };
      }
    | {
          /** Closes the guide manager dialog */
          type: 'close_manager';
      }
    | {
          /** Marks a guide as completed and stops it from running */
          type: 'complete_guide';
          payload: { guideId: GuideId };
      }
    | {
          /** Opens the guide manager dialog; optionally preselects a specific guide */
          type: 'open_manager';
          payload?: { guideId: GuideId };
      }
    | {
          /** Sets the completion state of a specific guide (without touching the running guide) */
          type: 'set_completed';
          payload: { guideId: GuideId; isCompleted: boolean };
      }
    | {
          /** Sets the completion state of every guide at once */
          type: 'set_all_completed';
          payload: { isCompleted: boolean };
      }
    | {
          /** Sets the discovery state of a specific guide (true hides its beacon) */
          type: 'set_discovered';
          payload: { guideId: GuideId; isDiscovered: boolean };
      }
    | {
          /** Sets the discovery state of every guide at once */
          type: 'set_all_discovered';
          payload: { isDiscovered: boolean };
      }
    | {
          /** Starts playing a guide: sets runningGuideId and resets that guide's step index to 0 */
          type: 'start_guide';
          payload: { guideId: GuideId };
      }
    | {
          /** Stops the currently running guide without marking it as completed */
          type: 'stop_guide';
      }
    | {
          /** Updates the step index for a specific guide (for future resume support) */
          type: 'set_step_index';
          payload: { guideId: GuideId; stepIndex: number };
      }
    | {
          /** Sets whether the "all guides completed" celebration has been seen */
          type: 'set_celebration_over';
          payload: { isCelebrationOver: boolean };
      };

/** Default completion state: all guides start as incomplete */
const DEFAULT_COMPLETION_STATE: GuideCompletionState =
    buildGuideBooleanMap(false);

/** Default discovery state: all guides start undiscovered, so every beacon shows */
const DEFAULT_DISCOVERY_STATE: GuideDiscoveryState =
    buildGuideBooleanMap(false);

/** Default step index state: all guides start at step 0 */
const DEFAULT_STEP_INDEX_STATE: GuideStepIndexState = {
    [GuideId.bomPdf]: 0,
    [GuideId.bomOverview]: 0,
    [GuideId.createEditBom]: 0,
    [GuideId.bomPurchaseOrders]: 0,
};

/** The initial state used when no persisted state exists */
const INITIAL_GUIDES_STATE: GuidesReducerState = {
    isManagerOpen: false,
    selectedManagerGuideId: GuideId.bomOverview,
    runningGuideId: null,
    completionByGuide: DEFAULT_COMPLETION_STATE,
    discoveryByGuide: DEFAULT_DISCOVERY_STATE,
    stepIndexByGuide: DEFAULT_STEP_INDEX_STATE,
    isCelebrationOver: false,
};

/**
 * Pure reducer function for the guides slice.
 * Handles all guide lifecycle transitions: opening/closing the manager,
 * starting/stopping/completing guides, step tracking, and session resets.
 *
 * @param state - The current guides state
 * @param action - The action to apply
 * @returns The new guides state (never mutates the input)
 */
const guidesReducer = (
    state: GuidesReducerState,
    action: GuidesReducerAction
): GuidesReducerState => {
    switch (action.type) {
        case 'open_manager':
            // Open the dialog and optionally jump to a specific guide
            return {
                ...state,
                isManagerOpen: true,
                ...(action.payload?.guideId !== undefined && {
                    selectedManagerGuideId: action.payload.guideId,
                }),
            };

        case 'close_manager':
            return { ...state, isManagerOpen: false };

        case 'select_guide':
            return { ...state, selectedManagerGuideId: action.payload.guideId };

        case 'start_guide':
            // Begin playing a guide: set it as running and reset its step index
            return {
                ...state,
                runningGuideId: action.payload.guideId,
                stepIndexByGuide: {
                    ...state.stepIndexByGuide,
                    [action.payload.guideId]: 0,
                },
            };

        case 'stop_guide':
            // User closed the guide without finishing — clear running state only
            return { ...state, runningGuideId: null };

        case 'complete_guide':
            // Guide finished: mark as completed and stop running
            return {
                ...state,
                runningGuideId: null,
                completionByGuide: {
                    ...state.completionByGuide,
                    [action.payload.guideId]: true,
                },
            };

        case 'set_step_index':
            return {
                ...state,
                stepIndexByGuide: {
                    ...state.stepIndexByGuide,
                    [action.payload.guideId]: action.payload.stepIndex,
                },
            };

        case 'set_completed':
            return {
                ...state,
                completionByGuide: {
                    ...state.completionByGuide,
                    [action.payload.guideId]: action.payload.isCompleted,
                },
            };

        case 'set_all_completed':
            return {
                ...state,
                completionByGuide: buildGuideBooleanMap(
                    action.payload.isCompleted
                ),
            };

        case 'set_discovered':
            return {
                ...state,
                discoveryByGuide: {
                    ...state.discoveryByGuide,
                    [action.payload.guideId]: action.payload.isDiscovered,
                },
            };

        case 'set_all_discovered':
            return {
                ...state,
                discoveryByGuide: buildGuideBooleanMap(
                    action.payload.isDiscovered
                ),
            };

        case 'set_celebration_over':
            return {
                ...state,
                isCelebrationOver: action.payload.isCelebrationOver,
            };
    }
};

export {
    DEFAULT_COMPLETION_STATE,
    DEFAULT_DISCOVERY_STATE,
    DEFAULT_STEP_INDEX_STATE,
    guidesReducer,
    INITIAL_GUIDES_STATE,
};
export type {
    GuideCompletionState,
    GuideDiscoveryState,
    GuidesReducerAction,
    GuidesReducerState,
    GuideStepIndexState,
};
